import { createContext, useContext } from 'react';

import type { ItemType } from '~/components/common/SelectCheckbox';

type GlobalFiltersOptions = {
  portfolios: ItemType[];
  serviceProviders: ItemType[];
  serviceProvidersByPortfolio: Record<string, ItemType[]>;
  regions: ItemType[];
};

export type GlobalFiltersSelection = {
  portfolios: string[];
  serviceProviders: string[];
  regions: string[];
  date: {
    start: Date;
    end: Date;
  };
};

/**
 * `silent` marks a change the app made on the user's behalf rather than one
 * they applied — reconciling a selection a route cannot express, say. It still
 * updates the filters and the URL; it just isn't counted as filter engagement.
 */
export type GlobalFiltersChangeOptions = { silent?: boolean };

export type GlobalFilterContext = {
  options: GlobalFiltersOptions;
  selectedFilters: GlobalFiltersSelection;
  onGlobalFiltersChange: (
    filters: GlobalFiltersSelection,
    options?: GlobalFiltersChangeOptions,
  ) => void;
};

const defaultDate = new Date(0);

const initialContextState: GlobalFilterContext = {
  options: {
    portfolios: [],
    serviceProviders: [],
    serviceProvidersByPortfolio: {},
    regions: [],
  },
  selectedFilters: {
    portfolios: [],
    serviceProviders: [],
    regions: [],
    date: { start: defaultDate, end: defaultDate },
  },
  onGlobalFiltersChange: () => {},
};

export const useGlobalFilterContext = () => {
  const context = useContext(GlobalFilterContext);
  if (!context) {
    //throw new Error('useGlobalFilterContext must be used within a GlobalFilterContextProvider');
    console.error('useGlobalFilterContext must be used within a GlobalFilterContextProvider');
  }
  return context;
};

export const GlobalFilterContext = createContext<GlobalFilterContext>(initialContextState);
